import React, { useState, useEffect } from "react";
import axios from "utils/axios";
import { FaPlus } from "react-icons/fa6";
import { FiTrash2 } from "react-icons/fi";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import { showToast } from "components/ui/toast";
import ImageContainer from "components/upload/ImageContainer";
import Lightbox from "components/ui/Lightbox";

const extractS3KeyFromUrl = (url) => {
  if (!url) return null;
  const match = url.match(/\/([^/]+)$/);
  return match ? match[1] : null;
};

const FloorplanUpload = ({ eventProp }) => {
  const [images, setImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [uploadProgress, setUploadProgress] = useState({});
  const [allImagesUploaded, setAllImagesUploaded] = useState(false);
  const [editConfirm, setEditConfirm] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  // "3D floorplan" tick: a rendered 3D plan rather than a 2D drawing. Saved
  // with the delivery so the agent's AI revisions keep it 3D.
  const [is3d, setIs3d] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const fileInputId = `floorplan-upload-${eventProp._id}`;

  useEffect(() => {
    setAllImagesUploaded(
      images.length > 0 && images.every((img) => img.isUploaded)
    );
  }, [images]);

  useEffect(() => {
    setIs3d(Boolean(eventProp.floorplan?.some((img) => img.is3d)));
    if (eventProp.floorplan) {
      setImages(
        eventProp.floorplan.map((img, i) => ({
          ...img,
          // The stored entry's own id — `id` below is a local key only.
          floorplanId: img.id,
          isSaved: true,
          id: i + 1,
          url: img.url,
          name: img.name || `Image ${i + 1}`,
          folder: img.folder || "raw",
          isUploaded: true,
          type: img.type || "web",
        }))
      );
    }
  }, [eventProp]);

  const handleFileUpload = async (event, type) => {
    const file = Array.from(event.target.files)[0];
    if (!file) return;
    const newFile = {
      id: Date.now(),
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      isUploaded: false,
      s3Key: null,
      eventId: eventProp._id,
      type,
    };
    setImages((prev) => [...prev.filter((img) => img.type !== type), newFile]);
    event.target.value = "";
    try {
      const res = await axios.post("/admin/prepareMarketing/fileUpload", {
        files: [{ fileName: type + "-" + file.name, fileType: file.type }],
        propertyId: eventProp?.propertyId?._id,
        folderPath: "floorplan",
      });
      const presigned = res.data.presignedUrls[0];
      await axios.put(presigned.url, file, {
        headers: { "Content-Type": file.type },
        onUploadProgress: (e) => {
          setUploadProgress((prev) => ({
            ...prev,
            [newFile.id]: Math.round((e.loaded * 100) / e.total),
          }));
        },
      });
      setImages((prev) =>
        prev.map((img) =>
          img.id === newFile.id
            ? { ...img, isUploaded: true, s3Key: presigned.key }
            : img
        )
      );
    } catch (error) {
      showToast("error", "Failed to upload file");
      setImages((prev) => prev.filter((img) => img.id !== newFile.id));
    }
  };

  const deleteImage = async (imageId) => {
    const image = images.find((img) => img.id === imageId);
    if (!image) return;
    try {
      if ((image.isUploaded && image.s3Key) || image.url) {
        await axios.post("/admin/prepareMarketing/deleteS3File", {
          key: image.s3Key || extractS3KeyFromUrl(image.url),
          name: image.name,
          eventId: eventProp?._id,
          propertyId: eventProp?.propertyId?._id,
          field: "floorplan",
        });
      }
      setImages((prev) => prev.filter((img) => img.id !== imageId));
    } catch (error) {
      showToast("error", "Failed to delete image");
    }
  };

  // A saved floorplan is deleted as a pair: the web entry takes its print
  // twin with it, on the arrays and on a rental's Media rows alike.
  const savedWeb = images.find((img) => img.type === "web" && img.isSaved);
  const deleteSavedFloorplan = async () => {
    if (!savedWeb) return;
    setDeleteLoading(true);
    try {
      if (savedWeb.mediaId) {
        await axios.delete(`/admin/media/${savedWeb.mediaId}`);
      } else {
        await axios.delete(
          `/admin/prepareMarketing/${eventProp?.propertyId?._id}/floorplan/${savedWeb.floorplanId}`
        );
      }
      setImages((prev) => prev.filter((img) => !img.isSaved));
      setConfirmDelete(false);
      showToast("success", "Floorplan deleted");
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message || "Failed to delete floorplan"
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const sendToReview = async () => {
    try {
      setNotifyLoading(true);
      const finalImages = images
        .filter((img) => img.s3Key)
        .map((img, i) => ({
          id: i + 1,
          s3Key: img.s3Key,
          name: img.name,
          size: img.size,
          type: img.type,
        }));
      const res = await axios.put(
        `/admin/prepareMarketing/${eventProp?.propertyId?._id}`,
        {
          floorplan: {
            eventId: eventProp?._id,
            eventStatus: eventProp?.status,
            is3d,
            images: finalImages,
          },
        }
      );
      if (res.data.success) {
        showToast("success", "Sent to review");
        setEditConfirm(false);
      }
    } catch (error) {
      showToast("error", "Failed to send for review");
    } finally {
      setNotifyLoading(false);
    }
  };

  const renderUploadBox = (type) => {
    const existing = images.find((img) => img.type === type);
    if (existing) {
      // Saved floorplans delete through the trash icon (with confirmation);
      // the X stays for a file staged here but not yet sent to review.
      const deletesAsPair = existing.isSaved && !!savedWeb;
      return (
        <div key={existing.id} className="flex flex-col gap-1">
          <ImageContainer
            image={existing}
            showDelete={!deletesAsPair}
            onDelete={(id) => deleteImage(id)}
            onPreview={() =>
              setLightboxIndex(
                images.findIndex((img) => img.id === existing.id)
              )
            }
            viewMode="grid"
            progress={uploadProgress[existing.id] || 0}
            eventId={eventProp?._id}
            propertyId={eventProp?.propertyId?._id}
            revisionType="floorplan"
          />
          {deletesAsPair && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={deleteLoading}
              className="self-end rounded p-1.5 text-red-500 hover:bg-red-50 disabled:opacity-40"
              title="Delete floorplan"
              aria-label="Delete floorplan"
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      );
    }
    return (
      <div
        key={`${type}-${fileInputId}`}
        className="flex items-center justify-center border border-darkgray border-dashed h-20 relative rounded"
      >
        <input
          type="file"
          onChange={(e) => handleFileUpload(e, type)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          accept="image/*"
        />
        <div className="flex flex-col items-center text-darkergray">
          <FaPlus className="mb-1" size={12} />
          <p className="text-xs capitalize">{type}</p>
        </div>
      </div>
    );
  };

  return (
    <>
      {eventProp.oldFloorplan && (
        <div className="grid gap-3 mb-3 grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          <div className="flex flex-col gap-2">
            <p className="font-semibold text-xs">Old Floorplan</p>
            <ImageContainer
              image={eventProp.oldFloorplan}
              viewMode="grid"
              showDownload
              showDelete={false}
              onPreview={() => setLightboxIndex(0)}
              revisionType="floorplan"
            />
          </div>
        </div>
      )}
      <div className="grid gap-3 mb-3 grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {renderUploadBox("web")}
        {renderUploadBox("print")}
      </div>
      <label className="flex items-start gap-2 mb-3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5 accent-black"
          checked={is3d}
          onChange={(e) => setIs3d(e.target.checked)}
        />
        <span className="text-xs text-darkgray leading-snug">
          <span className="text-black font-medium">3D floorplan</span> — tick
          if this is a rendered 3D plan, so AI revisions keep it 3D
        </span>
      </label>
      {images.length > 0 && (
        <div className="flex justify-end">
          <Button
            onClick={() => {
              if (
                !images.find((img) => img.type === "web" && img.isUploaded) ||
                !images.find((img) => img.type === "print" && img.isUploaded)
              ) {
                showToast("error", "Both web and print images needed");
                return;
              }
              setEditConfirm(true);
            }}
            className="black-button flex"
            disabled={!allImagesUploaded}
          >
            {!allImagesUploaded ? (
              <>
                <i className="fa-solid fa-spinner animate-spin mr-2" />{" "}
                Uploading
              </>
            ) : (
              "Send to Review"
            )}
          </Button>
        </div>
      )}
      <Lightbox
        images={images}
        currentIndex={lightboxIndex}
        isOpen={lightboxIndex >= 0}
        onClose={() => setLightboxIndex(-1)}
      />
      <Modal
        isOpen={confirmDelete}
        onClose={() => !deleteLoading && setConfirmDelete(false)}
        title="Delete Floorplan"
      >
        <div className="flex flex-col space-y-4">
          <p>
            Delete this floorplan (web and print) and all of its revisions? It
            will also be removed from any brochure, signboard or gallery that
            uses it.
          </p>
          <p className="italic">
            <strong>Note:</strong> This cannot be undone.
          </p>
          <div className="w-full flex gap-2 justify-end">
            <Button
              onClick={() => setConfirmDelete(false)}
              className="gray-button"
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={deleteSavedFloorplan}
              className="black-button"
              loading={deleteLoading}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={editConfirm}
        onClose={() => setEditConfirm(false)}
        title="Send to Review Confirmation"
      >
        <div className="flex flex-col space-y-4">
          <p>Are you sure you want to proceed?</p>
          <p className="italic">
            <strong>Note:</strong> Once sent, it cannot be undone.
          </p>
          <div className="w-full flex gap-2 justify-end">
            <Button
              onClick={() => setEditConfirm(false)}
              className="gray-button"
            >
              No
            </Button>
            <Button
              onClick={sendToReview}
              className="black-button"
              loading={notifyLoading}
            >
              Yes
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FloorplanUpload;
